import type { CSSProperties } from 'react';
// Copied from apps/web/src/components/Coin.tsx; keep the two in step.

// Brand marks: the orange "G" coin mascot, the small inline coin glyph, and marker swatches.

/** The signature coin. Decorative; place 2–4 per page at different sizes and tilts. */
export function CoinMascot({
  size = 96,
  rotate = -12,
  float = false,
  className = '',
}: {
  size?: number;
  rotate?: number;
  float?: boolean;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      aria-hidden="true"
      className={`pointer-events-none select-none ${className}`}
      style={
        {
          '--r': `${rotate}deg`,
          transform: `rotate(${rotate}deg)`,
          animation: float ? 'float 6s ease-in-out infinite' : undefined,
        } as CSSProperties
      }
    >
      <ellipse cx="53" cy="53" rx="44" ry="44" fill="#111110" />
      <circle cx="50" cy="50" r="44" fill="#ff6b1a" stroke="#111110" strokeWidth="2.5" />
      <circle
        cx="50"
        cy="50"
        r="34"
        fill="none"
        stroke="#111110"
        strokeWidth="1.5"
        opacity="0.35"
      />
      <text
        x="50"
        y="51"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="'Bricolage Grotesque Variable', sans-serif"
        fontWeight="800"
        fontSize="46"
        fill="#111110"
      >
        G
      </text>
    </svg>
  );
}

/** Inline coin for amounts. Sized to the surrounding text. */
export function CoinGlyph({ className = 'h-[1em] w-[1em]' }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className={`inline-block shrink-0 ${className}`}>
      <circle cx="10" cy="10" r="8.5" fill="#ff6b1a" stroke="#111110" strokeWidth="1.5" />
      <path
        d="M12.6 7.2A3.6 3.6 0 1 0 13.6 10h-3.2"
        fill="none"
        stroke="#111110"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

const SWATCH = { coin: '#ff6b1a', sun: '#ffc21a', mint: '#2fbf71', berry: '#ef3b2d' } as const;

/** Small square color marker used as a bullet or a full stop. */
export function Swatch({ color = 'coin' }: { color?: keyof typeof SWATCH }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-[0.32em] w-[0.32em] translate-y-[-0.08em] rounded-[2px] align-baseline"
      style={{ background: SWATCH[color] }}
    />
  );
}

/** Amount in coins with the glyph; orange because money is orange. */
export function Coins({ value, className = '' }: { value: number; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 font-bold tabular-nums ${className}`}>
      <CoinGlyph />
      {value.toLocaleString('en-US')}
    </span>
  );
}
